import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import OTPInput from "@/components/OTPInput";

/**
 * Step-up do painel interno: exige um email OTP (enviado pro owners@) a cada
 * sessão de aba, ALÉM do login (senha + TOTP). O token fica em sessionStorage,
 * então sobrevive a refresh mas limpa ao fechar a aba.
 * Só renderiza children depois de destravado — assim as chamadas de dados dos
 * filhos já carregam o X-Panel-Token.
 */
export default function PanelGate({ children }) {
  const t = useT();
  const { user, panelRequestOtp, panelVerifyOtp } = useAuth();
  const [unlocked, setUnlocked] = useState(!!sessionStorage.getItem("ratchet_panel_token"));
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const sendCode = async () => {
    setErr(""); setSending(true);
    try { await panelRequestOtp(); }
    catch (e) { setErr(e?.response?.data?.detail || t("panel.gate.err")); }
    finally { setSending(false); }
  };

  useEffect(() => {
    if (!unlocked) sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (value) => {
    const c = value ?? code;
    setErr(""); setLoading(true);
    try { await panelVerifyOtp(c); setUnlocked(true); }
    catch (e) { setErr(e?.response?.data?.detail || t("panel.gate.err")); setCode(""); }
    finally { setLoading(false); }
  };

  if (unlocked) return children;

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-sm space-y-5" data-testid="panel-gate">
        <div className="flex items-center gap-2 text-primary">
          <ShieldCheck className="w-5 h-5" />
          <div className="overline">{t("panel.gate.overline")}</div>
        </div>
        <div>
          <h1 className="font-display font-black text-3xl tracking-tighter">{t("panel.gate.title")}</h1>
          <p className="text-sm text-white/50 mt-2">
            {t("panel.gate.subtitle").replace("{email}", user?.email || "")}
          </p>
        </div>
        <OTPInput value={code} onChange={setCode} onComplete={verify} disabled={loading} error={!!err} />
        {err && <div className="text-sm text-destructive font-mono text-center" data-testid="panel-gate-error">{err}</div>}
        <button disabled={loading || code.length < 6} onClick={() => verify()} className="btn-primary w-full" data-testid="panel-gate-verify">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("panel.gate.verify")}
        </button>
        <button onClick={sendCode} disabled={sending} className="text-sm text-primary hover:underline disabled:text-white/30 w-full text-center" data-testid="panel-gate-resend">
          {sending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : t("panel.gate.resend")}
        </button>
      </div>
    </div>
  );
}
